import { CustomModal } from "components";
import { Button } from "reactstrap";
import { FormEntry } from "components";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import apiService from "services/api";
import { Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { getDataURIMimeType, mimeTypes } from "utils/getDataURIMimeType";
import { openBase64InNewTab } from "utils/openBase64InNewTab";
import { useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";
import useImageModal from "hooks/useImageModal";
import { useNotificationContext } from "contexts";

const NewAttendanceModal = ({ show, data, hideModal, refetch }) => {
	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		reset,
	} = useForm({
		defaultValues: {
			attendanceDate: "",
			attendanceType: "",
			attendanceTime: "",
			remark: "",
			base64ProofImgs: [],
		},
	});
	const { createNotification } = useNotificationContext();
	const {
		openModal,
		closeModal: closeImageModal,
		ImageModal,
	} = useImageModal();
	const {
		fields: base64ProofImgs,
		append: appendProofImg,
		remove: removeProofImg,
	} = useFieldArray({
		control,
		name: "base64ProofImgs",
	});
	const { data: centreOptions, isLoading: isLoadingCentres } = useQuery(
		["centre"],
		() =>
			apiService.student.centres().then((centres) =>
				centres.map((centre) => ({
					label: centre.shortName,
					value: centre.id,
					data: centre,
				})),
			),
	);

	const proofImgPathsRef = useRef(null);

	const appendFileToUploadedImg = async (e) => {
		if (!e.target.files.length) return;

		const imgURI = await encodeImageFileAsURL(e.target.files[0]);
		appendProofImg({ data: imgURI });
		e.target.value = "";
	};

	const removeFileFromUploadedImg = async (index) => removeProofImg(index);

	const { mutate: addAttendanceApplication, isLoading } = useMutation(
		(data) =>
			apiService.attendance.application.create({
				...data,
				base64ProofImgs: base64ProofImgs.map((resource) => resource.data),
			}),
		{
			onSuccess: () => {
				createNotification({
					type: "success",
					message: "Successfully applied for staff attendance",
				});
				refetch();
				closeImageModal();
				hideModal();
			},
		},
	);

	useEffect(() => {
		register("base64ProofImgs", {
			required: { value: true, message: "Proof image is required" },
		});
	}, [register]);

	useEffect(() => {
		if (!show) reset();
	}, [show, reset]);

	return (
		<>
			<ImageModal />
			<CustomModal
				show={show}
				hideModal={hideModal}
				compact={true}
				title="Add Attendance Application"
				children
				buttons={
					<div className="d-flex justify-center">
						<Button
							disabled={isLoading}
							color="info"
							type="submit"
							form="attendance-application-form"
						>
							Add
						</Button>
						<Button color="secondary" type="button" onClick={hideModal}>
							Cancel
						</Button>
					</div>
				}
				triggerAlertWhenClose={false}
				triggerAlertMessageWhenClose=""
				centered={true}
				zIndex={0}
			>
				<form
					onSubmit={handleSubmit((data) => addAttendanceApplication(data))}
					id="attendance-application-form"
				>
					<div className="px-4 text-white mt-4 mb-3">
						<FormEntry
							formLabel="Attendance Date"
							register={register}
							name="attendanceDate"
							error={errors.attendanceDate}
							type="date"
							required={true}
						/>
						<FormEntry
							formLabel="Time"
							register={register}
							name="attendanceTime"
							type="time"
							step="60"
							error={errors.attendanceTime}
							required={true}
						/>
						<FormEntry
							register={register}
							name="remark"
							type="text"
							formLabel="Remark"
							placeholder="Reason of manually adding attendance"
							required
							error={errors.remark}
						/>
						<div className="d-flex align-items-center p-2 mb-2">
							<small className="font-weight-bold mr-3">Attendance Type:</small>
							<span className="mr-4">
								<input
									type="radio"
									{...register("attendanceType", {
										required: {
											value: true,
											message: "Select an attendance Type",
										},
									})}
									value="tap-in"
									className="mr-2"
								/>
								<small className="text-white">Tap In</small>
							</span>
							<span>
								<input
									type="radio"
									{...register("attendanceType", {
										required: {
											value: true,
											message: "Select an attendance Type",
										},
									})}
									value="tap-out"
									className="mr-2"
								/>
								<small className="text-white">Tap Out</small>
							</span>
						</div>
						{errors?.attendanceType && (
							<div className="text-center mb-4">
								<small className="text-danger">
									{errors?.attendanceType?.message}
								</small>
							</div>
						)}
						<div className="mb-4">
							<Controller
								control={control}
								rules={{
									required: { value: true, message: "Select a centre" },
								}}
								name="centreId"
								render={({ field: { value, onChange } }) => (
									<Select
										className="react-select info mb-3"
										classNamePrefix="react-select"
										isClearable={true}
										value={centreOptions?.find(
											(centre) => centre.value === value,
										)}
										onChange={(val) => onChange(val.value)}
										placeholder="Select centre...."
										options={centreOptions || []}
										isDisabled={isLoadingCentres}
									/>
								)}
							/>
							{errors?.centreId && (
								<div className="text-center">
									<small className="text-danger">
										{errors?.centreId?.message}
									</small>
								</div>
							)}
						</div>
						<div
							className="px-3 py-2 pb-4 rounded-lg"
							style={{ border: "1px solid #ADB5DB" }}
						>
							<h5 className=" font-weight-bold text-white mb-1">
								Proof Images
							</h5>
							<input
								type="file"
								className="form-control d-none"
								onChange={appendFileToUploadedImg}
								accept="application/pdf, image/*"
								ref={proofImgPathsRef}
							/>
							{errors.base64ProofImgs && (
								<small className="text-danger">
									{errors.base64ProofImgs?.message}
								</small>
							)}
							<div className="mt-3">
								<small
									className="border border-light rounded px-3 py-2 text-light"
									style={{ cursor: "pointer" }}
									onClick={() => {
										proofImgPathsRef.current?.click();
									}}
								>
									Click to upload
								</small>
							</div>

							{Boolean(base64ProofImgs?.length) && (
								<div className="row mt-3 ">
									{base64ProofImgs?.map((resource, index) => (
										<div className="col-4" key={index}>
											<span
												className="border border-danger"
												style={{ cursor: "pointer", borderRadius: 5 }}
												onClick={() => removeFileFromUploadedImg(index)}
											>
												<FiX className="text-danger" />
												<small className="text-danger mx-1">Remove</small>
											</span>
											<div className="mt-1">
												<small>
													{getDataURIMimeType(resource.data) !==
													mimeTypes.pdf ? (
														<img
															src={resource.data}
															className="img-fluid rounded text-white"
															alt="Resource Uploaded"
															onClick={() => openModal(resource.data)}
														/>
													) : (
														<span
															onClick={() => openBase64InNewTab(resource.data)}
															style={{
																cursor: "pointer",
																borderRadius: 5,
																padding: 2,
															}}
															className="border border-success text-success px-1"
														>
															View Resource {index + 1}
														</span>
													)}
												</small>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</form>
			</CustomModal>
		</>
	);
};

export default NewAttendanceModal;
